/**
 * Evaluations — Informe individual del jugador.
 *
 * Two evaluation periods per season: October and May. Items rated 1-4
 * (4=Excelente · 3=Adecuado · 2=En desarrollo · 1=Necesita mejorar).
 *
 * Catalog is dynamic per player (depends on team category + position).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  FileText, Save, FileDown, Trophy, ChevronLeft, Sparkles,
  GitCompare, ArrowUp, ArrowDown, Minus, Pencil, Plus, X, ListChecks,
  ClipboardList, Dumbbell, CalendarCheck, Timer, TrendingUp, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const SCALE = [
  { value: 4, label: "Destacado",        cls: "bg-emerald-100 text-emerald-700 border-emerald-300 ring-emerald-500" },
  { value: 3, label: "Adecuado",         cls: "bg-blue-100 text-blue-700 border-blue-300 ring-blue-500" },
  { value: 2, label: "En desarrollo",    cls: "bg-amber-100 text-amber-700 border-amber-300 ring-amber-500" },
  { value: 1, label: "Necesita mejorar", cls: "bg-rose-100 text-rose-700 border-rose-300 ring-rose-500" },
];

const SCALE_HINT = {
  4: "Lo realiza con soltura y de forma constante en competición.",
  3: "Lo realiza correctamente en la mayoría de situaciones.",
  2: "Lo realiza con ayuda o de forma intermitente.",
  1: "Aún no lo realiza o comete errores frecuentes.",
};

const POTENTIAL_TONE = {
  muy_alto: "bg-emerald-100 text-emerald-700 border-emerald-300",
  alto:     "bg-blue-100 text-blue-700 border-blue-300",
  medio:    "bg-amber-100 text-amber-700 border-amber-300",
  en_desarrollo: "bg-slate-100 text-slate-700 border-slate-300",
};

function currentSeasonId() {
  const d = new Date();
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(2)}-${String(y + 1).slice(2)}`;
}

export default function Evaluations({ teams }) {
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Auto-pick the first team — computed during render, not via useEffect
  const effectiveTeamId = selectedTeamId || (teams && teams.length > 0 ? teams[0].id : "");

  useEffect(() => {
    if (!effectiveTeamId) return;
    let cancel = false;
    api.get(`/players?team_id=${effectiveTeamId}`)
      .then((r) => {
        if (cancel) return;
        setPlayers((r.data || []).sort((a, b) => (a.dorsal || 99) - (b.dorsal || 99)));
        setSelectedPlayer(null);
      })
      .catch((e) => !cancel && toast.error(formatApiError(e)));
    return () => { cancel = true; };
  }, [effectiveTeamId]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Nunito" }}>
            Informes individuales
          </h2>
          <p className="text-sm text-slate-500">
            Dos evaluaciones por temporada (Octubre · Mayo) — valoración 1-4, por categoría y posición.
          </p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</Label>
          <Select value={effectiveTeamId} onValueChange={setSelectedTeamId}>
            <SelectTrigger className="min-w-[240px]" data-testid="eval-team-select">
              <SelectValue placeholder="Selecciona equipo" />
            </SelectTrigger>
            <SelectContent>
              {(teams || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedPlayer ? (
        <PlayerGrid players={players} onSelect={setSelectedPlayer} />
      ) : (
        <PlayerEvaluation
          player={selectedPlayer}
          team={(teams || []).find((t) => t.id === selectedPlayer.team_id)}
          onBack={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
}

function PlayerGrid({ players, onSelect }) {
  if (!players || players.length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-slate-400 italic">
        Sin jugadores en este equipo.
      </CardContent></Card>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {players.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p)}
          className="text-left border border-slate-200 rounded-lg p-4 bg-white hover:border-purple-400 hover:shadow-md transition"
          data-testid={`eval-player-${p.id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-black flex items-center justify-center">
              #{p.dorsal || "-"}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-800 truncate">{p.name}</div>
              <div className="text-xs text-slate-500 truncate">
                {p.position || "Sin posición"} · {p.birth_year ? `nac. ${p.birth_year}` : "?"}
              </div>
            </div>
            <FileText className="h-4 w-4 text-purple-500 ml-auto" />
          </div>
        </button>
      ))}
    </div>
  );
}

const GRADE_TONE = {
  Excelente: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Destacado: "bg-emerald-100 text-emerald-700 border-emerald-300",
  Notable: "bg-blue-100 text-blue-700 border-blue-300",
  Adecuado: "bg-blue-100 text-blue-700 border-blue-300",
  Bien: "bg-amber-100 text-amber-700 border-amber-300",
  Mejorable: "bg-rose-100 text-rose-700 border-rose-300",
};

function StatTile({ icon: Icon, label, value, sub, empty }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`autodata-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
        <Icon className="h-3.5 w-3.5 text-purple-500" /> {label}
      </div>
      {empty ? (
        <div className="mt-1 text-sm text-slate-400 italic">Sin datos</div>
      ) : (
        <>
          <div className="mt-1 text-lg font-black text-slate-800 leading-none">{value}</div>
          {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </>
      )}
    </div>
  );
}

const LIST_TONE = {
  emerald: "text-emerald-600",
  amber: "text-amber-600",
  purple: "text-purple-600",
};

function ListField({ title, field, icon: Icon, tone, values, onChange, placeholder }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`h-4 w-4 ${LIST_TONE[tone] || "text-purple-600"}`} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {[0, 1].map((i) => (
          <Input
            key={i}
            value={(values || [])[i] || ""}
            onChange={(e) => onChange(field, i, e.target.value)}
            placeholder={`${placeholder} (${i + 1})`}
            data-testid={`eval-${field}-${i}`}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function AutoDataCard({ data }) {
  if (!data) return null;
  const att = data.attendance;
  const mt = data.matches;
  const phys = data.physical_tests || [];
  const hist = (data.evaluations_history || []).filter((h) => h.overall_rating != null);
  return (
    <Card data-testid="auto-data-card" className="border-purple-200 bg-purple-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
          <ClipboardList className="h-4 w-4 text-purple-600" />
          Datos automáticos
          <span className="text-[10px] font-normal text-slate-400">· de otros módulos, no editable</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile icon={CalendarCheck} label="Asistencia" empty={!att}
            value={att ? `${att.pct}%` : null}
            sub={att ? `${att.attended}/${att.total_trainings} entrenos` : null} />
          <StatTile icon={Trophy} label="Partidos" empty={!mt}
            value={mt ? `${mt.played_team}` : null}
            sub={mt ? `convocado ${mt.called} · jugó ${mt.participated}` : null} />
          <StatTile icon={Timer} label="Minutos" empty={!mt}
            value={mt ? `${mt.minutes}'` : null}
            sub={mt ? `${mt.minutes_pct}% de los posibles` : null} />
          <StatTile icon={TrendingUp} label="Participación" empty={!mt}
            value={mt ? `${mt.participation_pct}%` : null}
            sub={mt ? `partidos con minutos` : null} />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
            <Dumbbell className="h-3.5 w-3.5 text-purple-500" /> Tests físicos
          </div>
          {phys.length === 0 ? (
            <div className="text-sm text-slate-400 italic">Sin datos</div>
          ) : (
            <div className="space-y-2" data-testid="autodata-physical">
              {phys.map((pt) => (
                <div key={pt.evaluation} className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-slate-600 min-w-[90px]">
                    {pt.label} <span className="font-normal text-slate-400">{pt.date}</span>
                  </span>
                  {pt.items.map((it) => (
                    <span key={it.id} className={`text-[11px] px-1.5 py-0.5 rounded border ${GRADE_TONE[it.grade] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {it.name.replace(/\.$/, "")}: <b>{it.value}{it.unit}</b>{it.grade ? ` · ${it.grade}` : ""}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {hist.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="autodata-history">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
              <TrendingUp className="h-3.5 w-3.5 text-purple-500" /> Evolución (valoración general)
            </div>
            <div className="flex flex-wrap gap-2">
              {hist.map((h, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                  {h.season} {h.period === "octubre" ? "Oct" : "May"}: <b>{h.overall_rating}</b>/4
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function PlayerEvaluation({ player, team, onBack }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [catalog, setCatalog] = useState(null);
  const [itemsEditor, setItemsEditor] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [period, setPeriod] = useState("octubre");
  const [season] = useState(currentSeasonId());
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null);
  const [prevObjectives, setPrevObjectives] = useState([]);
  // Consolidated form state — single object so we only setState once per fetch.
  const [form, setForm] = useState({
    scores: {}, singleChoices: {}, globalRatings: {}, potential: "", observations: "",
    strengths: [], improvements: [], objectives: [], extraObservations: "",
    template: null, loaded: false,
  });
  const { scores, singleChoices, globalRatings, potential, observations, loaded } = form;
  const updateForm = (patch) => { setDirty(true); setForm((f) => ({ ...f, ...patch })); };
  const [saving, setSaving] = useState(false);

  const draftKey = `eval_draft::${player.id}::${season}::${period}`;

  const setScores = (next) => updateForm({ scores: typeof next === "function" ? next(form.scores) : next });
  const setSingleChoice = (area, val) => updateForm({ singleChoices: { ...form.singleChoices, [area]: form.singleChoices[area] === val ? "" : val } });
  const setGlobalRatings = (next) => updateForm({ globalRatings: typeof next === "function" ? next(form.globalRatings) : next });
  const setPotential = (v) => updateForm({ potential: v });
  const setObservations = (v) => updateForm({ observations: v });
  const setListItem = (field, i, val) => updateForm({
    [field]: Object.assign([...(form[field] || [])], { [i]: val }),
  });

  const [autoData, setAutoData] = useState(null);
  // Automatic data pulled from the player's other modules (read-only).
  useEffect(() => {
    let cancel = false;
    setAutoData(null);
    api.get(`/evaluations/auto-data/${player.id}?season=${encodeURIComponent(season)}`)
      .then((r) => { if (!cancel) setAutoData(r.data); })
      .catch(() => { if (!cancel) setAutoData(null); });
    return () => { cancel = true; };
  }, [player.id, season]);

  // Load catalog (areas) for this player
  const reloadCatalog = async () => {
    const r = await api.get(`/evaluations/catalog/for-player/${player.id}`);
    setCatalog(r.data);
  };
  useEffect(() => {
    let cancel = false;
    api.get(`/evaluations/catalog/for-player/${player.id}`)
      .then((r) => { if (!cancel) setCatalog(r.data); })
      .catch((e) => !cancel && toast.error(formatApiError(e)));
    return () => { cancel = true; };
  }, [player.id]);

  // Load existing evaluation for the selected period
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/evaluations?player_id=${player.id}&season=${season}&period=${period}`);
        if (cancel) return;
        const existing = (r.data || [])[0];
        setForm({
          scores: existing?.scores || {},
          singleChoices: existing?.single_choices || {},
          globalRatings: existing?.global_ratings || {},
          potential: existing?.potential || "",
          observations: existing?.observations || "",
          template: existing?.template || null,
          strengths: existing?.strengths || [],
          improvements: existing?.improvements || [],
          objectives: existing?.objectives || [],
          extraObservations: existing?.extra_observations || "",
          loaded: true,
        });
        setDirty(false);
        // Offer to restore a local draft if present for this period.
        try {
          const raw = localStorage.getItem(`eval_draft::${player.id}::${season}::${period}`);
          setDraft(raw ? JSON.parse(raw) : null);
        } catch { setDraft(null); }
        // Previous objectives (carry-over): most recent prior evaluation with objectives.
        try {
          const all = await api.get(`/evaluations?player_id=${player.id}`);
          const order = { octubre: 0, mayo: 1 };
          const cur = `${season}:${order[period]}`;
          const cand = (all.data || [])
            .filter((e) => (e.objectives || []).length && `${e.season}:${order[e.period]}` < cur)
            .sort((a, b) => `${b.season}:${order[b.period]}`.localeCompare(`${a.season}:${order[a.period]}`));
          setPrevObjectives(cand[0]?.objectives || []);
        } catch { setPrevObjectives([]); }
      } catch (err) {
        if (!cancel) {
          toast.error(formatApiError(err));
          setForm((f) => ({ ...f, loaded: true }));
        }
      }
    })();
    return () => { cancel = true; };
  }, [player.id, season, period]);

  const setItemScore = (area, idx, val) => {
    setScores((s) => {
      const areaMap = { ...(s[area] || {}) };
      if (areaMap[idx] === val) {
        delete areaMap[idx];
      } else {
        areaMap[idx] = val;
      }
      return { ...s, [area]: areaMap };
    });
  };

  const onSave = async () => {
    if (!catalog) return;
    setSaving(true);
    try {
      await api.post("/evaluations", {
        player_id: player.id,
        team_id: player.team_id,
        season,
        period,
        catalog_kind: catalog.catalog_kind,
        catalog_key: catalog.catalog_key,
        scores,
        single_choices: singleChoices,
        global_ratings: globalRatings,
        potential: potential || null,
        observations,
        strengths: form.strengths || [],
        improvements: form.improvements || [],
        objectives: form.objectives || [],
        extra_observations: form.extraObservations || "",
      });
      toast.success("Informe guardado");
      setDirty(false);
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      setDraft(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Autosave a local draft while there are unsaved changes.
  useEffect(() => {
    if (!dirty || !loaded) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          scores: form.scores, singleChoices: form.singleChoices,
          globalRatings: form.globalRatings, potential: form.potential,
          observations: form.observations, strengths: form.strengths,
          improvements: form.improvements, objectives: form.objectives,
          extraObservations: form.extraObservations, template: form.template,
          savedAt: new Date().toISOString(),
        }));
      } catch { /* quota */ }
    }, 800);
    return () => clearTimeout(t);
  }, [dirty, loaded, form, draftKey]);

  // Warn before closing the tab/window with unsaved changes.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const restoreDraft = () => {
    if (!draft) return;
    setForm((f) => ({
      ...f,
      scores: draft.scores || {}, singleChoices: draft.singleChoices || {},
      globalRatings: draft.globalRatings || {}, potential: draft.potential || "",
      observations: draft.observations || "", strengths: draft.strengths || [],
      improvements: draft.improvements || [], objectives: draft.objectives || [],
      extraObservations: draft.extraObservations || "",
      template: draft.template || f.template,
    }));
    setDirty(true);
    setDraft(null);
  };
  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setDraft(null);
  };
  const guardedBack = () => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Salir sin guardar?")) return;
    onBack();
  };
  const guardedPeriod = (p) => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Cambiar de periodo sin guardar?")) return;
    setPeriod(p);
  };

  const onDownloadPdf = async () => {
    try {
      // Save first to make sure backend has the latest data
      if (loaded) await onSave();
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const url = `${BACKEND}/api/export/player/${player.id}/evaluation-pdf?season=${encodeURIComponent(season)}&period=${period}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sectionAvg = (key, area) => {
    if (area.type === "single_choice" || area.options) return null;
    const m = scores[key] || {};
    const vals = (area.items || []).map((_, i) => m[i] ?? m[String(i)]).filter((v) => v >= 1 && v <= 4);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1).replace(".", ",");
  };

  const completeness = useMemo(() => {
    if (!catalog) return 0;
    const activeAreas = form.template || catalog.areas;
    let total = 0;
    let filled = 0;
    Object.entries(activeAreas).forEach(([k, a]) => {
      if (a.type === "single_choice" || a.options) {
        total += 1;
        if (singleChoices[k]) filled += 1;
        return;
      }
      total += a.items.length;
      const m = scores[k] || {};
      filled += Object.values(m).filter((v) => v && v >= 1 && v <= 4).length;
    });
    if (!catalog.simple_report) {
      total += (catalog.global_rating_areas || []).length + 1; // +1 = potential
      filled += Object.values(globalRatings).filter((v) => v && v >= 1 && v <= 4).length;
      if (potential) filled += 1;
    }
    return total ? Math.round((filled / total) * 100) : 0;
  }, [catalog, form.template, scores, singleChoices, globalRatings, potential]);

  const overallRating = useMemo(() => {
    if (!catalog?.has_overall) return null;
    const areas = form.template || catalog.areas;
    const weights = catalog.overall_weights || {};
    let num = 0, den = 0;
    Object.entries(areas).forEach(([k, a]) => {
      if (a.type === "single_choice" || a.options) return;
      const m = scores[k] || {};
      const vals = (a.items || []).map((_, i) => m[i] ?? m[String(i)]).filter((v) => v >= 1 && v <= 4);
      if (!vals.length) return;
      const avg = vals.reduce((x, y) => x + y, 0) / vals.length;
      const w = Number(weights[k] ?? 1);
      if (w <= 0) return;
      num += w * avg; den += w;
    });
    return den ? Math.round((num / den) * 100) / 100 : null;
  }, [catalog, form.template, scores]);

  if (!catalog || !loaded) {
    return <div className="text-sm text-slate-400 py-10 text-center italic">Cargando informe…</div>;
  }

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={guardedBack} data-testid="eval-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div>
            <div className="text-xl font-black text-slate-900 flex items-center gap-2">
              #{player.dorsal || "-"} {player.name}
              {dirty && (
                <span className="text-[11px] font-bold text-amber-600 flex items-center gap-1" data-testid="eval-dirty">
                  <AlertTriangle className="h-3.5 w-3.5" /> sin guardar
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500">
              {team?.name} · {(catalog.team_category || "").toUpperCase()} · {player.position || "Sin posición"}
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Periodo</Label>
            <Select value={period} onValueChange={guardedPeriod}>
              <SelectTrigger className="w-44" data-testid="eval-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(catalog.periods || []).map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Temporada</Label>
            <div className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700 font-mono w-24 text-center">
              {season}
            </div>
          </div>
          <Button onClick={onSave} disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="eval-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar"}
          </Button>
          <Button onClick={onDownloadPdf} variant="outline" data-testid="eval-pdf">
            <FileDown className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          {isAdmin && (
            <Button onClick={() => setItemsEditor(true)} variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="eval-items-editor-btn">
              <ListChecks className="h-4 w-4 mr-1.5" /> Editar ítems
            </Button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3" data-testid="eval-progress">
        <Progress value={completeness} className="h-2 flex-1" />
        <span className="text-xs font-bold text-purple-600 w-28 text-right">{completeness}% completo</span>
      </div>

      {/* Draft restore banner */}
      {draft && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="eval-draft-banner">
          <span className="text-sm text-amber-800">
            Tienes un borrador local sin guardar de este informe.
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800"
                    onClick={restoreDraft} data-testid="eval-draft-restore">Restaurar</Button>
            <Button size="sm" variant="ghost" className="text-slate-500"
                    onClick={discardDraft} data-testid="eval-draft-discard">Descartar</Button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="resumen" data-testid="eval-tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="evaluacion" data-testid="eval-tab-evaluacion">Evaluación</TabsTrigger>
          <TabsTrigger value="evolucion" data-testid="eval-tab-evolucion">Evolución</TabsTrigger>
          <TabsTrigger value="observaciones" data-testid="eval-tab-observaciones">Observaciones</TabsTrigger>
        </TabsList>

        {/* ---------- RESUMEN ---------- */}
        <TabsContent value="resumen" className="space-y-4 mt-4" data-testid="eval-panel-resumen">
          <AutoDataCard data={autoData} />
        </TabsContent>

        {/* ---------- EVALUACIÓN ---------- */}
        <TabsContent value="evaluacion" className="space-y-4 mt-4" data-testid="eval-panel-evaluacion">
          {/* Scale legend */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="font-bold text-slate-500 mr-1">Escala:</span>
            {SCALE.map((sc) => (
              <span key={sc.value} className={`px-2 py-1 rounded border ${sc.cls}`} title={SCALE_HINT[sc.value]}>
                <b>{sc.value}</b> {sc.label}
              </span>
            ))}
            <span className="px-2 py-1 rounded border bg-slate-100 text-slate-500 border-slate-200">
              <b>N/O</b> No observado
            </span>
          </div>

          {catalog.has_overall && (
            <div className="flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-4 py-2" data-testid="eval-overall">
              <span className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-purple-600" /> Valoración general (ponderada)
              </span>
              <span className="text-lg font-black text-purple-700">
                {overallRating != null ? `${String(overallRating).replace(".", ",")}/4` : "—"}
              </span>
            </div>
          )}

      {/* Areas */}
      {Object.entries(form.template || catalog.areas).map(([key, area]) => (
        <Card key={key}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {area.label}
              {sectionAvg(key, area) && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200" data-testid={`eval-avg-${key}`}>
                  media {sectionAvg(key, area)}/4
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(area.type === "single_choice" || area.options) ? (
              <div className="flex flex-wrap gap-2" data-testid={`eval-single-${key}`}>
                {(area.options || []).map((opt) => {
                  const on = singleChoices[key] === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setSingleChoice(key, opt)}
                      className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${
                        on
                          ? "bg-purple-600 text-white border-purple-600 ring-2 ring-purple-200"
                          : "bg-white text-slate-500 border-slate-200 hover:border-purple-400"
                      }`}
                      data-testid={`eval-single-${key}-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            ) : (
              <ul className="space-y-2">
                {(area.items || []).map((item, idx) => {
                  const current = scores[key]?.[idx];
                  return (
                    <li key={idx} className="flex flex-wrap items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{item}</span>
                      <div className="flex gap-1">
                        {SCALE.map((sc) => (
                          <button
                            key={sc.value}
                            type="button"
                            onClick={() => setItemScore(key, idx, sc.value)}
                            className={`w-9 h-9 rounded-md text-sm font-bold border transition ${
                              current === sc.value
                                ? `${sc.cls} ring-2`
                                : "bg-white text-slate-400 border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`eval-item-${key}-${idx}-${sc.value}`}
                            title={sc.label}
                          >
                            {sc.value}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setItemScore(key, idx, 0)}
                          className={`h-9 px-2 rounded-md text-[11px] font-bold border transition ${
                            current === 0
                              ? "bg-slate-200 text-slate-700 border-slate-400 ring-2 ring-slate-400"
                              : "bg-white text-slate-400 border-slate-200 hover:border-slate-400"
                          }`}
                          data-testid={`eval-item-${key}-${idx}-0`}
                          title="No observado (no cuenta en la media)"
                        >
                          N/O
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
        </TabsContent>

        {/* ---------- EVOLUCIÓN ---------- */}
        <TabsContent value="evolucion" className="space-y-4 mt-4" data-testid="eval-panel-evolucion">
          <ComparisonView player={player} season={season} catalog={catalog} />
        </TabsContent>

        {/* ---------- OBSERVACIONES ---------- */}
        <TabsContent value="observaciones" className="space-y-4 mt-4" data-testid="eval-panel-observaciones">
          {prevObjectives.length > 0 && (
            <Card className="border-slate-200 bg-slate-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-slate-400" /> Objetivos del informe anterior
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1" data-testid="eval-prev-objectives">
                  {prevObjectives.map((o, i) => (<li key={i}>{o}</li>))}
                </ul>
              </CardContent>
            </Card>
          )}
          <ListField title="Fortalezas principales" field="strengths" icon={ArrowUp} tone="emerald"
            values={form.strengths} onChange={setListItem} placeholder="Comportamiento deportivo destacado…" />
          <ListField title="Aspectos a mejorar" field="improvements" icon={ArrowDown} tone="amber"
            values={form.improvements} onChange={setListItem} placeholder="Aspecto deportivo a mejorar…" />
          <ListField title="Objetivos para el próximo periodo" field="objectives" icon={Sparkles} tone="purple"
            values={form.objectives} onChange={setListItem} placeholder="Objetivo concreto y observable…" />
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Observaciones adicionales</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={form.extraObservations}
                onChange={(e) => updateForm({ extraObservations: e.target.value })}
                placeholder="Notas internas opcionales (comportamientos observables)…"
                rows={4}
                data-testid="eval-extra-observations"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sticky save bar */}
      <div className="sticky bottom-16 lg:bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 -mx-4 px-4 py-3 flex justify-end gap-2 z-20">
        <Button onClick={onSave} disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="eval-save-bottom">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar informe"}
        </Button>
        <Button onClick={onDownloadPdf} variant="outline" data-testid="eval-pdf-bottom">
          <FileDown className="h-4 w-4 mr-1.5" /> Descargar PDF
        </Button>
      </div>

      {isAdmin && (
        <ItemsEditorDialog
          open={itemsEditor}
          onClose={() => setItemsEditor(false)}
          catalog={catalog}
          onSaved={reloadCatalog}
        />
      )}
    </div>
  );
}


// -------------------- Items editor (admin) --------------------
// Editable targets grouped for the selector. Each target encodes kind + key.
const EDIT_GROUPS = [
  {
    group: "Categorías jóvenes",
    options: [
      { kind: "category", key: "prebenjamin", label: "Prebenjamín" },
      { kind: "category", key: "benjamin", label: "Benjamín" },
      { kind: "category", key: "alevin", label: "Alevín" },
      { kind: "category", key: "infantil", label: "Infantil" },
    ],
  },
  {
    group: "Por posición (Cadete · Juvenil · Aficionado)",
    options: [
      { kind: "position", key: "central", label: "Central" },
      { kind: "position", key: "lateral", label: "Lateral" },
      { kind: "position", key: "mediocentro", label: "Mediocentro" },
      { kind: "position", key: "extremo", label: "Extremo" },
      { kind: "position", key: "delantero", label: "Delantero" },
      { kind: "position", key: "portero", label: "Portero" },
    ],
  },
  {
    group: "Bloque común (todas las categorías)",
    options: [
      { kind: "common", key: "closure", label: "Impacto · Eficacia · Perfil EFC" },
    ],
  },
];

function labelForTarget(kind, key) {
  for (const g of EDIT_GROUPS) {
    const o = g.options.find((x) => x.kind === kind && x.key === key);
    if (o) return o.label;
  }
  return key;
}

function ItemsEditorDialog({ open, onClose, catalog, onSaved }) {
  const [fullCatalog, setFullCatalog] = useState(null);
  const [kind, setKind] = useState("category");
  const [key, setKey] = useState("alevin");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Default target to the currently open player's catalog when it is editable.
  useEffect(() => {
    if (open && catalog) {
      const k = catalog.catalog_kind;
      const key0 = catalog.catalog_key;
      const editable = EDIT_GROUPS.some((g) => g.options.some((o) => o.kind === k && o.key === key0));
      if (editable) { setKind(k); setKey(key0); }
    }
  }, [open, catalog]);

  // Load the full live catalog once when opened.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/evaluations/catalog")
      .then((r) => setFullCatalog(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  // Populate draft whenever the selected target or catalog changes.
  useEffect(() => {
    if (!fullCatalog) return;
    const areas = (fullCatalog[kind] && fullCatalog[kind][key]) || {};
    const d = {};
    Object.entries(areas).forEach(([k, a]) => {
      if (a.type === "single_choice" || a.options) {
        d[k] = { label: a.label || k, choice: true, options: [...(a.options || [])] };
      } else {
        d[k] = { label: a.label || k, choice: false, items: [...(a.items || [])] };
      }
    });
    setDraft(d);
  }, [fullCatalog, kind, key]);

  if (!catalog) return null;

  const selectTarget = (value) => {
    const [k, ky] = value.split("::");
    setKind(k); setKey(ky);
  };

  // Move an entry (item or option) up/down within its area list.
  const moveEntry = (areaKey, field, i, dir) => {
    setDraft((d) => {
      const arr = [...d[areaKey][field]];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, [areaKey]: { ...d[areaKey], [field]: arr } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const [areaKey, a] of Object.entries(draft)) {
        if (a.choice) {
          const options = (a.options || []).map((x) => x.trim()).filter(Boolean);
          await api.put(
            `/evaluations/catalog/${kind}/${encodeURIComponent(key)}?area_key=${encodeURIComponent(areaKey)}`,
            { label: a.label, type: "single_choice", options },
          );
        } else {
          const items = (a.items || []).map((x) => x.trim()).filter(Boolean);
          await api.put(
            `/evaluations/catalog/${kind}/${encodeURIComponent(key)}?area_key=${encodeURIComponent(areaKey)}`,
            { label: a.label, items },
          );
        }
      }
      // Refresh local copy so switching targets reflects saved changes.
      try {
        const r = await api.get("/evaluations/catalog");
        setFullCatalog(r.data);
      } catch { /* non-blocking refresh */ }
      toast.success(`Ítems de ${labelForTarget(kind, key)} actualizados`);
      await onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="items-editor-dialog">
        <DialogHeader>
          <DialogTitle>Editar ítems del informe</DialogTitle>
          <DialogDescription>
            Elige la categoría/grupo y edita sus ítems. Puedes añadir, borrar y reordenar (▲▼)
            tanto los ítems puntuados 1-4 como las opciones de selección única (p. ej. Complexión).
            Los cambios afectan a todos los informes de esa categoría.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-1">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Categoría / grupo a editar</Label>
          <Select value={`${kind}::${key}`} onValueChange={selectTarget}>
            <SelectTrigger data-testid="items-editor-target" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EDIT_GROUPS.map((g) => (
                <div key={g.group}>
                  <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold">{g.group}</div>
                  {g.options.map((o) => (
                    <SelectItem key={`${o.kind}::${o.key}`} value={`${o.kind}::${o.key}`}
                                data-testid={`items-editor-target-${o.kind}-${o.key}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-6 text-center">Cargando catálogo…</p>
        ) : (
        <div className="space-y-4">
          {Object.entries(draft).map(([areaKey, a]) => {
            const field = a.choice ? "options" : "items";
            const list = a[field] || [];
            const addLabel = a.choice ? "Añadir opción" : "Añadir ítem";
            return (
            <div key={areaKey} className="border border-slate-200 rounded-lg p-3" data-testid={`items-editor-area-${areaKey}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={a.label}
                  onChange={(e) => setDraft((d) => ({ ...d, [areaKey]: { ...d[areaKey], label: e.target.value } }))}
                  className="font-bold"
                  data-testid={`items-editor-label-${areaKey}`}
                />
                {a.choice && (
                  <span className="shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                    Selección única
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {list.map((it, i) => (
                  <div key={i} className="flex items-center gap-1" data-testid={`items-editor-row-${areaKey}-${i}`}>
                    <div className="flex flex-col">
                      <button type="button" disabled={i === 0}
                        onClick={() => moveEntry(areaKey, field, i, -1)}
                        className="text-slate-400 hover:text-purple-600 disabled:opacity-30 disabled:hover:text-slate-400"
                        data-testid={`items-editor-up-${areaKey}-${i}`} title="Subir">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" disabled={i === list.length - 1}
                        onClick={() => moveEntry(areaKey, field, i, 1)}
                        className="text-slate-400 hover:text-purple-600 disabled:opacity-30 disabled:hover:text-slate-400"
                        data-testid={`items-editor-down-${areaKey}-${i}`} title="Bajar">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <Input
                      value={it}
                      onChange={(e) => setDraft((d) => {
                        const arr = [...d[areaKey][field]]; arr[i] = e.target.value;
                        return { ...d, [areaKey]: { ...d[areaKey], [field]: arr } };
                      })}
                      data-testid={`items-editor-item-${areaKey}-${i}`}
                    />
                    <Button size="sm" variant="ghost" className="text-red-600 shrink-0"
                      onClick={() => setDraft((d) => ({ ...d, [areaKey]: { ...d[areaKey], [field]: d[areaKey][field].filter((_, j) => j !== i) } }))}
                      data-testid={`items-editor-del-${areaKey}-${i}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline"
                  onClick={() => setDraft((d) => ({ ...d, [areaKey]: { ...d[areaKey], [field]: [...d[areaKey][field], ""] } }))}
                  data-testid={`items-editor-add-${areaKey}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> {addLabel}
                </Button>
              </div>
            </div>
            );
          })}
          {Object.keys(draft).length === 0 && (
            <p className="text-sm text-slate-500 py-4 text-center">Esta categoría no tiene secciones editables.</p>
          )}
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={save} disabled={saving || loading} data-testid="items-editor-save">
            {saving ? "Guardando…" : "Guardar ítems"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Comparison view (Oct ↔ Mayo) --------------------
function ComparisonView({ player, season, catalog }) {
  const [state, setState] = useState({ oct: null, may: null, loading: true, key: "" });
  const [history, setHistory] = useState([]);
  const { oct, may, loading } = state;
  const reqKey = `${player.id}::${season}`;

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [r1, r2, rAll] = await Promise.all([
          api.get(`/evaluations?player_id=${player.id}&season=${season}&period=octubre`),
          api.get(`/evaluations?player_id=${player.id}&season=${season}&period=mayo`),
          api.get(`/evaluations?player_id=${player.id}`),
        ]);
        if (cancel) return;
        const pOrder = { octubre: 0, mayo: 1 };
        const pts = (rAll.data || [])
          .filter((e) => e.overall_rating != null)
          .sort((a, b) => `${a.season}:${pOrder[a.period] ?? 9}`.localeCompare(`${b.season}:${pOrder[b.period] ?? 9}`))
          .map((e) => ({ name: `${e.season} ${e.period === "octubre" ? "Oct" : "May"}`, valoracion: Number(e.overall_rating) }));
        setHistory(pts);
        setState({
          oct: (r1.data || [])[0] || null,
          may: (r2.data || [])[0] || null,
          loading: false,
          key: reqKey,
        });
      } catch (e) {
        if (!cancel) {
          toast.error(formatApiError(e));
          setState((s) => ({ ...s, loading: false, key: reqKey }));
        }
      }
    })();
    return () => { cancel = true; };
  }, [player.id, season, reqKey]);

  // Treat as loading if the data we have is for a different request key
  const showLoading = loading || state.key !== reqKey;

  const summary = useMemo(() => {
    if (!oct || !may) return null;
    let up = 0, down = 0, same = 0, missing = 0;
    Object.entries(catalog.areas).forEach(([k, area]) => {
      area.items.forEach((_, idx) => {
        const o = oct.scores?.[k]?.[idx] ?? oct.scores?.[k]?.[String(idx)];
        const m = may.scores?.[k]?.[idx] ?? may.scores?.[k]?.[String(idx)];
        if (!o || !m) { missing += 1; return; }
        if (m > o) up += 1;
        else if (m < o) down += 1;
        else same += 1;
      });
    });
    return { up, down, same, missing };
  }, [oct, may, catalog]);

  if (showLoading) return <div className="text-sm text-slate-400 py-10 text-center italic">Cargando comparativa…</div>;

  const chartBlock = (
    <Card data-testid="eval-evolution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-purple-600" /> Evolución de la valoración general
        </CardTitle>
      </CardHeader>
      <CardContent>
        {history.length >= 2 ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={history} margin={{ top: 8, right: 16, left: -12, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis domain={[1, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => [`${v} / 4`, "Valoración"]} />
              <ReferenceLine y={3} stroke="#cbd5e1" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="valoracion" stroke="#7E22CE" strokeWidth={3}
                dot={{ r: 4, fill: "#7E22CE" }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="py-8 text-center text-sm text-slate-400 italic">
            Guarda la valoración general de al menos dos periodos para ver la evolución.
          </p>
        )}
      </CardContent>
    </Card>
  );

  const bothMissing = !oct && !may;
  const onlyOne = (oct && !may) || (!oct && may);

  if (bothMissing) {
    return (
      <div className="space-y-4">
        {chartBlock}
        <Card><CardContent className="py-10 text-center text-slate-400 italic">
          Aún no hay datos para esta temporada. Guarda al menos una evaluación (Octubre o Mayo) para usar la comparativa.
        </CardContent></Card>
      </div>
    );
  }

  const onDownloadComparativoPdf = () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND}/api/export/player/${player.id}/evaluation-comparison-pdf?season=${encodeURIComponent(season)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="space-y-4">
      {chartBlock}
      <div className="flex justify-end">
        <Button
          onClick={onDownloadComparativoPdf}
          variant="outline"
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
          data-testid="eval-comparativa-pdf"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> Descargar PDF comparativo
        </Button>
      </div>
      {onlyOne && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 text-sm text-amber-800">
            Solo hay datos de <b>{oct ? "Octubre" : "Mayo"}</b>. La comparativa se activará cuando guardes el otro periodo.
          </CardContent>
        </Card>
      )}

      {summary && (
        <div className="grid grid-cols-3 gap-3" data-testid="eval-compare-summary">
          <SummaryTile icon={ArrowUp} label="Items mejorados" value={summary.up} tone="emerald" />
          <SummaryTile icon={Minus}   label="Items igual"      value={summary.same} tone="slate" />
          <SummaryTile icon={ArrowDown} label="Items en retroceso" value={summary.down} tone="rose" />
        </div>
      )}

      {/* Areas comparison */}
      {Object.entries(catalog.areas).map(([k, area]) => (
        <Card key={k}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{area.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left font-bold">Item</th>
                  <th className="px-2 py-2 text-center font-bold">Octubre</th>
                  <th className="px-2 py-2 text-center font-bold">Mayo</th>
                  <th className="px-2 py-2 text-center font-bold w-16">Δ</th>
                </tr>
              </thead>
              <tbody>
                {area.items.map((item, idx) => {
                  const o = oct?.scores?.[k]?.[idx] ?? oct?.scores?.[k]?.[String(idx)];
                  const m = may?.scores?.[k]?.[idx] ?? may?.scores?.[k]?.[String(idx)];
                  return (
                    <tr key={idx} className="border-b border-slate-100 last:border-0">
                      <td className="px-2 py-2 text-slate-700">{item}</td>
                      <td className="px-2 py-2 text-center"><ScoreBadge v={o} /></td>
                      <td className="px-2 py-2 text-center"><ScoreBadge v={m} /></td>
                      <td className="px-2 py-2 text-center"><DeltaBadge a={o} b={m} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {/* Global ratings comparison */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-purple-600" /> Valoración global final
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold">Área</th>
                <th className="px-2 py-2 text-center font-bold">Octubre</th>
                <th className="px-2 py-2 text-center font-bold">Mayo</th>
                <th className="px-2 py-2 text-center font-bold w-16">Δ</th>
              </tr>
            </thead>
            <tbody>
              {(catalog.global_rating_areas || []).map((a) => {
                const o = oct?.global_ratings?.[a.key];
                const m = may?.global_ratings?.[a.key];
                return (
                  <tr key={a.key} className="border-b border-slate-100 last:border-0">
                    <td className="px-2 py-2 text-slate-800 font-bold">{a.label}</td>
                    <td className="px-2 py-2 text-center"><ScoreBadge v={o} /></td>
                    <td className="px-2 py-2 text-center"><ScoreBadge v={m} /></td>
                    <td className="px-2 py-2 text-center"><DeltaBadge a={o} b={m} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Potencial */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" /> Potencial de desarrollo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <PotentialBox label="Octubre" value={oct?.potential} options={catalog.potential_options} />
            <PotentialBox label="Mayo"    value={may?.potential} options={catalog.potential_options} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const SCORE_COLOR = {
  4: "bg-emerald-100 text-emerald-700 border-emerald-300",
  3: "bg-blue-100 text-blue-700 border-blue-300",
  2: "bg-amber-100 text-amber-700 border-amber-300",
  1: "bg-rose-100 text-rose-700 border-rose-300",
};

function ScoreBadge({ v }) {
  if (!v) return <span className="text-slate-300">—</span>;
  return (
    <span className={`inline-block w-8 h-8 leading-8 text-center rounded font-bold border ${SCORE_COLOR[v]}`}>
      {v}
    </span>
  );
}

function DeltaBadge({ a, b }) {
  if (!a || !b) return <span className="text-slate-300">—</span>;
  const diff = b - a;
  if (diff === 0) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500"><Minus className="h-3 w-3" /> 0</span>;
  }
  if (diff > 0) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><ArrowUp className="h-3 w-3" /> +{diff}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-700"><ArrowDown className="h-3 w-3" /> {diff}</span>;
}

function SummaryTile({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <div className="text-3xl font-black">{value}</div>
    </div>
  );
}

function PotentialBox({ label, value, options }) {
  const found = (options || []).find((o) => o.key === value);
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="text-lg font-black text-purple-700">{found?.label || "—"}</div>
    </div>
  );
}

